import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PrimaryGrid } from '@ui/components/common/Grid';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { StyledMenu } from '@ui/components/common/Menu';
import ReassignVehicleDialog from '../ReassignVehicleDialog';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { getAssignedAttendeesByPickupLocationIdAsync, getUnassignedAttendeesByPickupLocationIdAsync, unassignAttendeeFromVehicleAsync, assignAttendeeToVehicleAsync } from '@application/features/attendeePickupAssignment/attendeePickupAssignmentThunk';
import { getAssignedFamilyFestAttendeesByPickupLocationIdAsync, getUnassignedFamilyFestAttendeesByPickupLocationIdAsync, unassignFamilyFestAttendeeFromRouteVehicleAsync } from '@application/features/familyFest/familyFestPickupAssignmentThunk';
import { assignAttendeeToRouteVehicleAsync } from '@application/features/eventVehicle/eventVehicleThunk';
import { getFamilyFestPickupLocationByIdAsync } from '@application/features/familyFest/familyFestPickupLocationThunk';
import { getPickupLocationByIdAsync } from '@application/features/pickupLocation/pickupLocationThunk';
import { toast } from 'react-toastify';
import { formatRegisteredDateOnly } from '@application/utilities/date-helper';
export const ASSIGNED_ATTENDEE_ROWS = [];
const LocationAssignedAttendeeTab = ({ filterOpen, onFilterOpenChange, searchValue, locationId, isFamilyFest }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { assignedAttendees: standardAssignedAttendees } = useAppSelector((state) => state.attendeePickupAssignment);
    const { assignedAttendees: familyFestAssignedAttendees } = useAppSelector((state) => state.familyFestPickupAssignment);
    const selectedCompanyIds = useAppSelector((state) => state.company.selectedCompanyId);
    const assignedAttendees = isFamilyFest ? familyFestAssignedAttendees : standardAssignedAttendees;
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);
    const [selectedAttendeeForReassign, setSelectedAttendeeForReassign] = useState(null);
    const [reassignVehicleOpen, setReassignVehicleOpen] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [sortModel, setSortModel] = useState([]);
    const [filters, setFilters] = useState({});
    useEffect(() => {
        if (locationId) {
            if (isFamilyFest) {
                const sort = sortModel[0];
                const sortFieldMap = {
                    company: 'companyname',
                    type: 'attendeetype',
                    registered: 'createdon'
                };
                dispatch(getAssignedFamilyFestAttendeesByPickupLocationIdAsync({
                    pickupLocationId: String(locationId),
                    pageNumber: paginationModel.page + 1,
                    pageSize: paginationModel.pageSize,
                    searchString: searchValue || undefined,
                    name: filters.name,
                    vehicleNumber: filters.vehicleNumber,
                    routeName: filters.routeName,
                    companyName: filters.company,
                    attendeeType: filters.type,
                    registeredDate: filters.registered,
                    sortBy: sort ? (sortFieldMap[sort.field] ?? sort.field) : undefined,
                    sortOrder: sort?.sort ?? 'desc'
                }));
            }
            else {
                dispatch(getAssignedAttendeesByPickupLocationIdAsync({
                    pickupLocationId: String(locationId),
                    pageNumber: paginationModel.page + 1,
                    pageSize: paginationModel.pageSize,
                    searchString: searchValue || undefined,
                    name: filters.name,
                    vehicleNumber: filters.vehicleNumber,
                    companyName: filters.company,
                    attendeeType: filters.type,
                    registeredDate: filters.registered
                }));
            }
        }
    }, [
        dispatch,
        locationId,
        paginationModel.page,
        paginationModel.pageSize,
        searchValue,
        filters,
        isFamilyFest,
        sortModel,
        selectedCompanyIds
    ]);
    const handleMenuClose = useCallback(() => {
        setMenuAnchorEl(null);
        setMenuRow(null);
    }, []);
    const handleSendToUnassigned = useCallback(async () => {
        handleMenuClose();
        if (menuRow) {
            if (isFamilyFest) {
                dispatch(unassignFamilyFestAttendeeFromRouteVehicleAsync([menuRow.id]))
                    .unwrap()
                    .then(() => {
                    toast.success(t('events.eventDetails.transportTab.attendeeUnassignedSuccessfully'));
                    if (locationId) {
                        setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
                        dispatch(getFamilyFestPickupLocationByIdAsync(String(locationId)));
                        dispatch(getAssignedFamilyFestAttendeesByPickupLocationIdAsync({
                            pickupLocationId: locationId,
                            pageNumber: 1,
                            pageSize: paginationModel.pageSize,
                            searchString: searchValue || undefined
                        }));
                        dispatch(getUnassignedFamilyFestAttendeesByPickupLocationIdAsync({
                            pickupLocationId: locationId,
                            pageNumber: 1,
                            pageSize: paginationModel.pageSize
                        }));
                    }
                })
                    .catch((error) => {
                    toast.error(error?.error || t('events.eventDetails.transportTab.attendeeUnassignFailed'));
                });
            }
            else {
                dispatch(unassignAttendeeFromVehicleAsync([menuRow.id]))
                    .unwrap()
                    .then((response) => {
                    if (response.isSuccess) {
                        toast.success(response.message || t('events.eventDetails.transportTab.attendeeUnassignedSuccessfully'));
                        // Refresh assigned list, unassigned list, and location summary counts
                        if (locationId) {
                            setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
                            dispatch(getPickupLocationByIdAsync(String(locationId)));
                            dispatch(getAssignedAttendeesByPickupLocationIdAsync({
                                pickupLocationId: locationId,
                                pageNumber: 1,
                                pageSize: paginationModel.pageSize,
                                searchString: searchValue || undefined
                            }));
                            dispatch(getUnassignedAttendeesByPickupLocationIdAsync({
                                pickupLocationId: locationId,
                                pageNumber: 1,
                                pageSize: paginationModel.pageSize
                            }));
                        }
                    }
                    else {
                        toast.error(response.message || t('events.eventDetails.transportTab.attendeeUnassignFailed'));
                    }
                })
                    .catch((error) => {
                    toast.error(error?.error || t('events.eventDetails.transportTab.attendeeUnassignFailed'));
                });
            }
        }
    }, [handleMenuClose, menuRow, dispatch, isFamilyFest, locationId, paginationModel.pageSize, searchValue, t]);
    const handleReassignVehicle = useCallback(() => {
        if (menuRow) {
            setSelectedAttendeeForReassign(menuRow);
        }
        handleMenuClose();
        setReassignVehicleOpen(true);
    }, [handleMenuClose, menuRow]);
    const handleReassignConfirm = useCallback(async (vehicle) => {
        if (selectedAttendeeForReassign) {
            if (isFamilyFest) {
                dispatch(assignAttendeeToRouteVehicleAsync({
                    assignmentIds: [selectedAttendeeForReassign.id],
                    routeVehicleId: vehicle.id
                }))
                    .unwrap()
                    .then(() => {
                    toast.success(t('events.eventDetails.transportTab.attendeeReassignedSuccessfully'));
                    if (locationId) {
                        setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
                        dispatch(getAssignedFamilyFestAttendeesByPickupLocationIdAsync({
                            pickupLocationId: locationId,
                            pageNumber: 1,
                            pageSize: paginationModel.pageSize,
                            searchString: searchValue || undefined
                        }));
                    }
                    setSelectedAttendeeForReassign(null);
                    setReassignVehicleOpen(false);
                })
                    .catch((error) => {
                    toast.error(error?.error || t('events.eventDetails.transportTab.attendeeReassignFailed'));
                });
            }
            else {
                dispatch(assignAttendeeToVehicleAsync({
                    attendeeIds: [selectedAttendeeForReassign.id],
                    vehicleId: vehicle.id
                }))
                    .unwrap()
                    .then((response) => {
                    if (response.isSuccess) {
                        toast.success(response.message || t('events.eventDetails.transportTab.attendeeReassignedSuccessfully'));
                        // Refresh the list
                        if (locationId) {
                            setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
                            dispatch(getAssignedAttendeesByPickupLocationIdAsync({
                                pickupLocationId: locationId,
                                pageNumber: 1,
                                pageSize: paginationModel.pageSize,
                                searchString: searchValue || undefined
                            }));
                        }
                        setSelectedAttendeeForReassign(null);
                        setReassignVehicleOpen(false);
                    }
                    else {
                        toast.error(response.message || t('events.eventDetails.transportTab.attendeeReassignFailed'));
                    }
                })
                    .catch((error) => {
                    toast.error(error?.error || t('events.eventDetails.transportTab.attendeeReassignFailed'));
                });
            }
        }
    }, [selectedAttendeeForReassign, dispatch, isFamilyFest, locationId, paginationModel.pageSize, searchValue, t]);
    const rowMenuActions = useMemo(() => [
        {
            text: t('events.eventDetails.transportTab.sendToUnassigned'),
            to: handleSendToUnassigned,
            group: true
        },
        {
            text: t('events.eventDetails.transportTab.reassignVehicle'),
            to: handleReassignVehicle,
            group: true
        }
    ], [t, handleSendToUnassigned, handleReassignVehicle]);
    const columns = useMemo(() => [
        {
            field: 'name',
            type: 'string',
            headerName: t('events.eventDetails.transportTab.name'),
            flex: 1,
            minWidth: 150,
            filterField: 'name',
            renderCell: (params) => (_jsxs(Box, { display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 40, children: [_jsx(Typography, { variant: 'body2', children: params.row.name || '—' }), params.row.hasBirthdayOnEventDate && (_jsx(Typography, { variant: 'caption', color: 'text.secondary', children: t('events.eventDetails.transportTab.birthday') }))] }))
        },
        {
            field: 'vehicleNumber',
            headerName: t('events.eventDetails.transportTab.vehicleNumber'),
            flex: 0.9,
            minWidth: 130,
            filterField: 'vehicleNumber',
            renderCell: (params) => {
                return params.row.vehicleNumber || '—';
            }
        },
        {
            field: 'company',
            type: 'string',
            headerName: t('events.eventDetails.transportTab.company'),
            flex: 1,
            minWidth: 140,
            filterField: 'company',
            renderCell: (params) => {
                return params.row.companyName || params.row.company || '—';
            }
        },
        {
            field: 'type',
            type: 'string',
            headerName: t('events.eventDetails.transportTab.type'),
            flex: 0.8,
            minWidth: 110,
            filterField: 'type',
            renderCell: (params) => {
                return params.row.attendeeType || '—';
            }
        },
        {
            field: 'registered',
            headerName: t('events.eventDetails.transportTab.registered'),
            flex: 1.2,
            minWidth: 160,
            filterField: 'registered',
            renderCell: (params) => {
                return formatRegisteredDateOnly(params.row.createdDate);
            }
        },
        {
            field: 'actions',
            headerName: '',
            flex: 0.35,
            minWidth: 70,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (_jsx(Box, { display: 'flex', justifyContent: 'flex-end', width: '100%', children: _jsx(IconButton, { size: 'small', onClick: (e) => {
                        e.stopPropagation();
                        setMenuAnchorEl(e.currentTarget);
                        setMenuRow(params.row);
                    }, children: _jsx(MoreHorizIcon, {}) }) }))
        }
    ], [t]);
    return (_jsxs(Box, { children: [_jsx(PrimaryGrid, { showHeaderControls: false, showNewHeaderControls: true, filterOpen: filterOpen, onFilterOpenChange: onFilterOpenChange, columns: columns, rows: assignedAttendees?.items || [], paginationModel: paginationModel, onPaginationModelChange: (newModel) => {
                    setPaginationModel(newModel);
                }, onApplyFilters: (newFilters) => {
                    setFilters(newFilters);
                    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
                }, paginationMode: 'server', rowCount: assignedAttendees?.totalCount || 0, sortingMode: 'server', sortModel: sortModel, onSortModelChange: (model) => {
                    setSortModel(model);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }, pageSizeOptions: [10, 25, 50, 100], hideFooter: false, sx: {
                    height: 'calc(100dvh - 464px)'
                } }), _jsx(StyledMenu, { anchorEl: menuAnchorEl, open: Boolean(menuAnchorEl), onClose: handleMenuClose, actions: rowMenuActions, anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' } }), _jsx(ReassignVehicleDialog, { open: reassignVehicleOpen, onClose: () => {
                    setSelectedAttendeeForReassign(null);
                    setReassignVehicleOpen(false);
                }, onConfirm: handleReassignConfirm, title: t('events.eventDetails.transportTab.reassignVehicle'), isFamilyFest: isFamilyFest })] }));
};
export default LocationAssignedAttendeeTab;
